import React, { useEffect } from 'react';
import Shared from 'screens/shared';
import { actions, ACTIONS_TYPES } from 'redux/local';
import {
  // eslint-disable-next-line max-len
  ALB_HTTP_RULE_CONDITIONS_CONDITION,
  ALB_HTTP_RULE_CONDITIONS_CONDITION$,
  ALB_HTTP_RULE_CONDITIONS_TYPE,
  ALB_HTTP_RULE_CONDITIONS_TYPE$,
  ALB_HTTP_RULE_REDIRECT_STATUS_CODE_RANGE,
  ALB_HTTP_RULE_TYPE,
  ALB_HTTP_RULE_TYPE$,
  ALB_PROTOCOL,
  VALIDATION,
} from 'constant';
import {
  enumToArray, _t,
} from 'utils';
import { withRouter } from 'react-router-dom';

export default withRouter(({ item: editProps = {}, match }) => {
  const { dcId, albId, id } = match.params;

  delete editProps.id;
  const defaultValues = {
    protocol: ALB_PROTOCOL.HTTP,
    httpRules: [],
    serverCertificates: [],
    healthCheck: { clientTimeout: 50000 },
    ...editProps,
  };

  const fields = [
    {
      name: 'name',
      type: 'text',
      title: _t('Name'),
    },
    {
      name: 'protocol',
      type: 'dropdown',
      title: 'Protocol',
      options: enumToArray(ALB_PROTOCOL),
    },
    {
      name: 'listenerIp',
      type: 'dropdown',
      title: 'Listener IP (inbound)',
      getOptionsStoreMap: (store) => store[ACTIONS_TYPES.APPLICATION_LOAD_BALANCER.GET]?.properties?.ips || [],
    },
    {
      name: 'listenerPort',
      type: 'port',
      title: 'Listener Port (inbound)',
    },
    {
      name: 'healthCheck',
      type: 'object',
      title: 'Connection Settings',
      fields: [
        {
          name: 'isHealthCheck',
          type: 'switch',
          title: 'Change Default Connection Settings?',
        },
        {
          name: 'clientTimeout',
          type: 'number',
          title: 'Client Timeout (MS)',
          // eslint-disable-next-line max-len
          helpText: 'ClientTimeout is expressed in milliseconds. This inactivity timeout applies when the client is expected to acknowledge or send data. If unset the default of 50 seconds will be used.',
          setVisibility: (e) => e.isHealthCheck,
        },
      ],
    },
    {
      name: 'serverCertificates',
      type: 'array',
      isAddButton: true,
      title: 'Server Certificates',
      setItemTitle: (e) => e?.id?.properties?.name,
      icon: 'shield-alt',
      actionButtonName: 'Add Certificate',
      setValueFrom: (arr) => arr.map((e) => e?.id?.id),
      fields: [
        {
          name: 'id',
          type: 'common-selector',
          title: 'Certificate',
          itemIcon: 'shield-alt',
          setItemTitle: ({ properties }) => properties?.name,
          getItemsStoreMap: (e) => e[ACTIONS_TYPES.CERTIFICATE_MANAGER.GET_ALL],
          returnValuePropOnly: false,
          setValueTo: (e) => e?.id,
        },
      ],
      validations: [
        {
          name: 'id',
          type: VALIDATION.REQUIRED,
        },
      ],
    },
    {
      name: 'httpRules',
      type: 'array',
      title: 'HTTP Rules',
      setItemTitle: (e) => e?.name,
      setItemDetails: (e) => e?.type,
      isAddButton: true,
      icon: 'angle-double-right',
      actionButtonName: 'Add HTTP Rule',
      fields: [
        {
          name: 'name',
          type: 'text',
          title: 'Name',
        },
        {
          name: 'conditions',
          title: 'Conditions',
          setItemTitle: (e) => `${ALB_HTTP_RULE_CONDITIONS_TYPE$[e.type]} - ${ALB_HTTP_RULE_CONDITIONS_CONDITION$[e.condition]}`,
          setItemDetails: (e) => e.key,
          actionButtonName: 'Add Condition',
          type: 'array',
          isAddButton: true,
          icon: 'stream',
          fields: [
            {
              name: 'type',
              type: 'dropdown',
              options: enumToArray(ALB_HTTP_RULE_CONDITIONS_TYPE).map((e) => [e, ALB_HTTP_RULE_CONDITIONS_TYPE$[e]]),
              title: 'Source Type',
            },
            {
              name: 'key',
              type: 'text',
              title: 'Key',
              // eslint-disable-next-line max-len
              setVisibility: (e) => [ALB_HTTP_RULE_CONDITIONS_TYPE.COOKIE, ALB_HTTP_RULE_CONDITIONS_TYPE.HEADER, ALB_HTTP_RULE_CONDITIONS_TYPE.QUERY].includes(e.type),
            },
            {
              name: 'condition',
              type: 'dropdown',
              options: enumToArray(ALB_HTTP_RULE_CONDITIONS_CONDITION).map((e) => [e, ALB_HTTP_RULE_CONDITIONS_CONDITION$[e]]),
              title: 'Operation',
              setVisibility: (e) => e.type !== ALB_HTTP_RULE_CONDITIONS_TYPE.SOURCE_IP,
            },
            {
              name: 'value',
              type: 'text',
              title: 'Value',
              setVisibility: (e) => e.condition !== ALB_HTTP_RULE_CONDITIONS_CONDITION.EXISTS,
            },
            {
              name: 'negate',
              type: 'switch',
              title: 'Negate',
              helpText: 'Specifies whether the condition is negated or not',
            },
          ],
          validations: [
            { name: 'type', type: VALIDATION.REQUIRED },
            { name: 'condition', type: VALIDATION.REQUIRED },
            { name: 'key', type: VALIDATION.REQUIRED },
            { name: 'value', type: VALIDATION.REQUIRED },
          ],
        },
        {
          name: 'type',
          type: 'dropdown',
          options: enumToArray(ALB_HTTP_RULE_TYPE).map((e) => [e, ALB_HTTP_RULE_TYPE$[e]]),
          title: 'Action',
        },
        // FORWARD
        {
          name: 'targetGroup',
          type: 'common-selector',
          title: 'Target Group',
          itemIcon: 'crosshairs',
          setItemTitle: ({ properties }) => properties?.name,
          getItemsStoreMap: (e) => e[ACTIONS_TYPES.TARGET_GROUP.GET_ALL],
          setVisibility: (e) => e.type === ALB_HTTP_RULE_TYPE.FORWARD,
        },
        // REDIRECT
        {
          name: 'location',
          type: 'text',
          title: 'Redirect URL / Location',
          setVisibility: (e) => e.type === ALB_HTTP_RULE_TYPE.REDIRECT,
        },
        {
          name: 'statusCode',
          type: 'dropdown',
          title: 'Status Code',
          isNumber: true,
          // eslint-disable-next-line max-len
          updateProps: (e) => ({ values: e.type === ALB_HTTP_RULE_TYPE.REDIRECT ? [...ALB_HTTP_RULE_REDIRECT_STATUS_CODE_RANGE] : Array(400).fill('').map((_, i) => 200 + i) }),
          setVisibility: (e) => e.type === ALB_HTTP_RULE_TYPE.REDIRECT || e.type === ALB_HTTP_RULE_TYPE.STATIC,
        },
        {
          name: 'dropQuery',
          type: 'switch',
          title: 'Drop Query',
          setVisibility: (e) => e.type === ALB_HTTP_RULE_TYPE.REDIRECT,
        },
        // STATIC
        {
          name: 'responseMessage',
          type: 'textarea',
          title: 'Response Message',
          setVisibility: (e) => e.type === ALB_HTTP_RULE_TYPE.STATIC,
        },
      ],
      validations: [
        { name: 'name', type: VALIDATION.REQUIRED },
        { name: 'type', type: VALIDATION.REQUIRED },
        { name: 'conditions', type: VALIDATION.REQUIRED },
        { name: 'targetGroup', type: VALIDATION.REQUIRED },
        { name: 'statusCode', type: VALIDATION.REQUIRED },
        { name: 'responseMessage', type: VALIDATION.REQUIRED },
        { name: 'location', type: VALIDATION.REQUIRED },
      ],
    },
  ];

  const validations = [
    { name: 'name', type: VALIDATION.REQUIRED },
    { name: 'protocol', type: VALIDATION.REQUIRED },
    { name: 'listenerIp', type: VALIDATION.REQUIRED },
    { name: 'listenerPort', type: VALIDATION.REQUIRED },
  ];

  useEffect(() => {
    actions.certificateManager.getAll();
    actions.targetGroup.getAll();
    actions.applicationLoadbalancer.get({ dcId, id: albId || id });
  }, [dcId, id, albId]);

  const submitHandler = (values) => {
    const { healthCheck = {}, ...properties } = values;
    const { isHealthCheck = false } = healthCheck;
    if (isHealthCheck) {
      properties.clientTimeout = healthCheck.clientTimeout;
    }

    delete properties.healthCheck;

    console.log({
      albId, dcId, id, properties,
    });

    if (albId && id) {
      return actions.applicationLoadbalancer.updateForwardingRule({
        albId, dcId, id, properties,
      });
    }

    return actions.applicationLoadbalancer.createForwardingRule({ properties, dcId, id: albId || id });
  };

  return (
    <Shared.forms.Form
      fields={fields}
      fieldsValues={defaultValues}
      validations={validations}
      isCancel={false}
      onSubmit={submitHandler}
    />
  );
});
